import type { TranslationKeys } from '../types';

const es: TranslationKeys = {
  nav: {
    home: 'Inicio',
    blockExplorer: 'Explorador',
    swap: 'Intercambio',
    exchange: 'Intercambio cross-chain',
    docs: 'Docs',
    more: 'Más',
    close: 'Cerrar',
    bridge: 'Puente',
  },
  bridge: {
    title: 'Puente Igra',
    subtitle: 'Conecta KAS mediante puente entre Kaspa e Igra (cadena {chainId})',
    tabDeposit: 'Depositar KAS',
    tabDepositSub: 'KAS → iKAS',
    tabWithdraw: 'Retirar KAS',
    tabWithdrawSub: 'iKAS → KAS',
    chainKaspa: 'Kaspa',
    chainEvm: 'EVM (Igra)',
    useWallet: 'Usar billetera',
    gate: {
      installTitle: 'Conectar KasWare',
      installDesc:
        'Las transacciones del puente se firman completamente dentro de KasWare. Instala la extensión o abre esta página en la app de Telegram de KasWare.',
      disconnectedTitle: 'Billetera desconectada',
      connectWallet: 'Conectar billetera',
      connectTitle: 'Conectar {chain}',
      connectDesc: 'Autoriza la cuenta de {chain} en KasWare para continuar.',
      connecting: 'Conectando...',
      connectChain: 'Conectar {chain}',
    },
    deposit: {
      youSend: 'Envías (Kaspa L1)',
      youReceive: 'Recibes (Igra L2)',
      recipientLabel: 'Recibe iKAS en esta dirección de Igra',
      fromWallet: '(de la billetera)',
      badAddress: 'Introduce una dirección EVM válida: una dirección con mayúsculas debe superar la suma de comprobación EIP-55.',
      connectEvm: 'Conectar EVM para autocompletar',
      connectingEvm: 'Conectando EVM...',
      infoMin: 'Depósito mínimo: {min} KAS.',
      infoMining:
        'El id de transacción se mina en tu navegador para empezar con 97b1; normalmente tarda unos segundos.',
      infoMint: 'El iKAS se acuña después de que el secuenciador de Igra confirme el Entry en Kaspa.',
      phaseConnecting: 'Conectando a la red Kaspa...',
      phaseFetching: 'Obteniendo tus UTXO...',
      phaseMining: 'Minando un id de transacción válido... ({tries} intentos)',
      phaseSigning: 'Esperando tu firma en KasWare...',
      phaseBroadcasting: 'Difundiendo la transacción Entry...',
      cancelMining: 'Cancelar minado',
      successTitle: 'Depósito enviado',
      successMined: 'Id válido encontrado tras {tries} intentos (nonce {nonce}).',
      kaspaTx: 'Transacción de Kaspa:',
      prefixWarning:
        'El id de transacción difundido ya no empieza por 97b1. Igra podría no acreditar este depósito: guarda el id de transacción y contacta con soporte.',
      newDeposit: 'Nuevo depósito',
      cta: 'Depositar KAS en Igra',
      ctaAmount: 'Depositar {amount} KAS en Igra',
    },
    withdraw: {
      youBurn: 'Quemas (Igra L2)',
      paidOut: 'Pagado en (Kaspa L1)',
      payoutLabel: 'Dirección de pago KAS',
      infoLimits: 'Límites: {min} – {max} iKAS por retiro.',
      infoFeeZero: 'Comisión del protocolo: actualmente 0.',
      infoFeeQuoted: 'Comisión del protocolo: cotizada al solicitarla.',
      infoRate: '1 iKAS se quema para pagar exactamente 1 KAS (sin diferencial).',
      warningPrefix: 'Los retiros',
      warningStrong: 'no son instantáneos',
      warningRest:
        'el iKAS se quema en Igra y luego un comité multifirma libera los KAS en Kaspa tras revisar la solicitud. Envía solo importes que puedas esperar.',
      checking: 'Comprobando...',
      modalTitle: 'Confirmar retiro',
      modalBurn: 'Quemas',
      modalFee: 'Comisión del protocolo',
      modalPayout: 'KAS pagados a',
      modalAck:
        'Entiendo que la liberación es manual, la revisa un comité multifirma y no es instantánea.',
      modalConfirm: 'Confirmar y quemar iKAS',
      modalBack: 'Atrás',
      successTitle: 'Solicitud de salida confirmada en Igra',
      requestId: 'Id de solicitud de salida: {id}',
      igraTx: 'Transacción de Igra:',
      successPayout: 'Los KAS se enviarán a:',
      successNote: 'Los KAS se liberarán a tu dirección de pago tras la revisión multifirma.',
      newWithdrawal: 'Nuevo retiro',
      requesting: 'Solicitando retiro...',
      cta: 'Quemar iKAS y solicitar KAS',
      ctaAmount: 'Quemar {amount} iKAS y solicitar KAS',
    },
  },
  home: {
    tagline: 'KasWare Wallet',
    hero: {
      subtitle:
        'Una sola cartera autocustodiada para TON, Kaspa y cadenas EVM: sus claves nunca salen de su dispositivo.',
      chainsLabel: 'Redes compatibles',
      ctaDownload: 'Añadir a Chrome',
      ctaTelegram: 'Abrir en Telegram',
      note: 'Gratis, sin registro ni correo electrónico.',
    },
    pillars: {
      security: {
        title: 'Segura por diseño',
        desc: 'Las claves se generan y se cifran en su dispositivo. Cada transacción se analiza y se muestra completa antes de firmar.',
      },
      convenience: {
        title: 'Todo en un solo lugar',
        desc: 'Guarde, envíe, intercambie y haga bridge entre TON, Kaspa y EVM sin manejar varias carteras.',
      },
      friendly: {
        title: 'Pensada para personas',
        desc: 'Mensajes en lenguaje claro, direcciones legibles, temas claro y oscuro, y siete idiomas incluidos.',
      },
      reliable: {
        title: 'Fiable',
        desc: 'Historial verificable, varios endpoints de red y una cartera que sigue funcionando si un proveedor falla.',
      },
    },
    content: {
      downloadTitle: 'Descargar',
      securityTitle: 'Por qué sus fondos siguen siendo suyos',
      p1: 'KasWare es una cartera autocustodiada para Ton, Kaspa y EVM.',
      p2: 'Nunca tenemos acceso a sus fondos. Sus claves privadas están cifradas en su dispositivo con su contraseña y nunca se comparten con nadie.',
      p3: 'Todas las cuentas se derivan de forma determinística de su Frase de Recuperación Secreta, dándole propiedad y control totales.',
    },
  },
  about: {
    contact: 'Contáctanos',
    contacts: {
      twitter: 'Twitter',
      telegram: 'Telegram',
      discord: 'Discord',
      email: 'Correo electrónico',
    },
  },
  footer: {
    privacy: 'Política de Privacidad',
    copyright: '© {year} Flying Rabbit Tech Ltd',
  },
  explorer: {
    title: 'Explorador de Transacciones',
    subtitle: 'Busque registros de transacciones Kaspa y KRC-20 por dirección',
    timeRange: 'Rango de fechas:',
    to: 'a',
    clear: 'Limpiar',
    address: 'Dirección:',
    kasTab: 'Transferencias KAS',
    krc20Tab: 'Operaciones KRC-20',
    enterAddress: 'Ingrese una dirección Kaspa para ver las transacciones',
    loading: 'Cargando...',
    search: {
      placeholder: 'Ingrese dirección Kaspa (kaspa:q...)',
      button: 'Buscar',
    },
    tx: {
      loading: 'Cargando transacciones...',
      empty: 'No se encontraron transacciones para esta dirección',
      emptyInRange: 'No se encontraron transacciones para esta dirección en el rango de fechas seleccionado',
      failed: 'Error al cargar transacciones: {error}',
      retry: 'Reintentar',
    },
    krc20: {
      loading: 'Cargando operaciones KRC-20...',
      empty: 'No se encontraron operaciones KRC-20 para esta dirección',
      emptyInRange: 'No se encontraron operaciones KRC-20 para esta dirección en el rango de fechas seleccionado',
      failed: 'Error al cargar operaciones KRC-20: {error}',
      retry: 'Reintentar',
    },
  },
  languageNames: {
    en: 'EN',
    id: 'ID',
    fr: 'FR',
    zh_CN: '简体中文',
    zh_TW: '繁體中文',
    es: 'ES',
    th: 'TH',
  },
};

export default es;
